import type { Injector, Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type { Observable } from 'rxjs'
import { Subject, filter, mergeMap, take, takeUntil, map } from 'rxjs'
import type { TaskViewList } from '@awork/features/task/models/task-view-list.model'
import { TaskViewStore } from '@awork/features/task/state/task-view.store'
import { TaskQuery } from '@awork/features/task/state/task.query'
import { Task } from '@awork/features/task/models/task.model'

@Injectable({ providedIn: 'root' })
export class TaskViewQuery extends EntitySignalQuery<TaskViewList> {
  protected store: TaskViewStore
  private taskQuery = inject(TaskQuery)

  queryCancelled = new Subject<void>()

  constructor() {
    const store = inject(TaskViewStore)

    super(store)

    this.store = store
  }

  /**
   * Selects a Task View from the store
   * @param {Injector} injector Angular injector
   * @param {string} taskViewId
   * @returns {Observable<TaskViewList>}
   * @deprecated
   */
  selectTaskView(injector: Injector, taskViewId: string): Observable<TaskViewList> {
    return this.selectEntity(taskViewId, injector)
  }

  /**
   * Gets a Task View from the store
   * @param {string} taskViewId
   * @returns {Signal<TaskViewList>}
   */
  queryTaskView(taskViewId: string): Signal<TaskViewList> {
    return this.queryEntity(taskViewId)
  }

  /**
   * Select All Task Views from the store
   * @param {Injector} injector Angular injector
   * @param {boolean} shared
   * @returns {Observable<TaskViewList[]>}
   * @deprecated
   */
  selectAllTaskViews(injector: Injector, shared?: boolean): Observable<TaskViewList[]> {
    return this.selectAll(injector, {
      filterBy: taskView => {
        return shared !== undefined ? taskView.isShared === shared : true
      },
      sortBy: (a, b) => {
        return new Date(a.createdOn)?.getTime() - new Date(b.createdOn)?.getTime()
      }
    }).pipe(map(taskViews => this.mapEntities(taskViews)))
  }

  /**
   * Query All Task Views from the store
   * @param {boolean} shared
   * @returns {Signal<TaskViewList[]>}
   */
  queryAllTaskViews(shared?: boolean): Signal<TaskViewList[]> {
    return this.queryAll({
      filterBy: taskView => {
        return shared !== undefined ? taskView.isShared === shared : true
      },
      sortBy: (a, b) => {
        return new Date(a.createdOn)?.getTime() - new Date(b.createdOn)?.getTime()
      }
    })
  }

  /**
   * Select all tasks for a specific taskView
   * @param {Injector} injector Angular injector
   * @param {string} taskViewId
   * @param {boolean} [sync=false]
   * @returns {Observable<Task[]>}
   * @deprecated
   */
  selectAllTasks(injector: Injector, taskViewId: string, sync = false): Observable<Task[]> {
    return this.selectEntity(taskViewId, injector).pipe(
      filter(taskView => !!taskView && !!taskView.taskIds),
      mergeMap(taskView => {
        const taskIdsSet = new Set(taskView.taskIds)
        const taskQuery = this.taskQuery
          .selectAll(injector, {
            filterBy: task => taskIdsSet.has(task.id).valueOf(),
            sortBy: this.getTasksSortBy
          })
          .pipe(
            takeUntil(this.queryCancelled),
            map(tasks => tasks.map(task => Task.mapTask(task)))
          )

        return sync ? taskQuery.pipe(take(1)) : taskQuery
      })
    )
  }

  /**
   * Query all tasks for a specific taskView
   * @param {string} taskViewId
   * @returns {Signal<Task[]>}
   */
  queryAllTasks(taskViewId: string): Signal<Task[]> {
    return computed((): Task[] => {
      const taskView = this.queryEntity(taskViewId)

      if (!taskView()?.taskIds) {
        return []
      }

      const taskIdsSet = new Set(taskView().taskIds)
      const taskQuery = this.taskQuery.queryAll({
        filterBy: task => taskIdsSet.has(task.id).valueOf(),
        sortBy: this.getTasksSortBy
      })

      return taskQuery().map(task => Task.mapTask(task))
    })
  }

  /**
   * Get the tasks sort by due date or project and task name
   * @param {Task} taskA
   * @param {Task} taskB
   * @returns {number}
   */
  private getTasksSortBy(taskA: Task, taskB: Task): number {
    // Set dueOn order
    const taskAdueOnOrder = taskA.dueOn ? new Date(taskA.dueOn).getTime() : 0
    const taskBdueOnOrder = taskB.dueOn ? new Date(taskB.dueOn).getTime() : 0

    // order by due date descending
    // else order by project and task name
    if (taskAdueOnOrder && taskBdueOnOrder) {
      if (taskAdueOnOrder < taskBdueOnOrder) {
        return -1
      } else if (taskAdueOnOrder === taskBdueOnOrder) {
        return 0
      } else {
        return 1
      }
    } else {
      if (taskAdueOnOrder !== 0) {
        return -1
      } else if (taskBdueOnOrder !== 0) {
        return 1
      } else {
        // sort by project and then by task name for tasks which don't have a due date
        const taskAname = (taskA.project ? taskA.project.name + '-' : '') + taskA.name || ''
        const taskBname = (taskB.project ? taskB.project.name + '-' : '') + taskB.name || ''

        return taskAname.localeCompare(taskBname)
      }
    }
  }
}
